import { Routes, Route, Navigate, useLocation, type Location } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Home } from './routes/Home';
import { Browse } from './routes/Browse';
import { ItemDetail } from './routes/ItemDetail';
import { ItemModal } from './components/ItemModal';
import { Credits } from './routes/Credits';
import { CreditsPayment } from './routes/CreditsPayment';
import { CreditsSuccess } from './routes/CreditsSuccess';
import { HowItWorks } from './routes/HowItWorks';
import { SignIn } from './routes/SignIn';
import { SignUp } from './routes/SignUp';
import { Account } from './routes/Account';
import { Checkout } from './routes/Checkout';
import { Contact } from './routes/Contact';
import { Faq } from './routes/Faq';
import { VerifyId } from './routes/VerifyId';
import {
  PrivacyPolicy,
  TermsAndConditions,
  ReturnsPolicy,
  DamageLossPolicy,
  CookiePolicy,
  ReturnsAndDamage
} from './routes/Legal';
import { AdminShell } from './routes/admin/AdminShell';
import { RequireAuth } from './components/RequireAuth';
import { RequireAdmin } from './components/RequireAdmin';

export function App() {
  // Modal pattern: when a card is clicked from Browse, the Link sets
  // location.state.backgroundLocation = current location. We then run two
  // Routes trees — the underlying one matches against the BACKGROUND
  // location (so Browse stays mounted with its scroll preserved), and a
  // second tree matches against the actual URL to render the item modal
  // overlaid on top. A direct visit to /browse/:slug has no
  // backgroundLocation, so the regular page route renders normally.
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location } | null;
  const backgroundLocation = state?.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation ?? location}>
      {/* Storefront — uses the phone-app shell */}
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="browse" element={<Browse />} />
        <Route path="browse/:slug" element={<ItemDetail />} />
        <Route path="credits" element={<Credits />} />
        <Route
          path="credits/payment"
          element={
            <RequireAuth>
              <CreditsPayment />
            </RequireAuth>
          }
        />
        <Route
          path="credits/success"
          element={
            <RequireAuth>
              <CreditsSuccess />
            </RequireAuth>
          }
        />
        <Route path="how-it-works" element={<HowItWorks />} />
        <Route path="contact" element={<Contact />} />
        <Route path="faq" element={<Faq />} />
        <Route
          path="verify-id"
          element={
            <RequireAuth>
              <VerifyId />
            </RequireAuth>
          }
        />

        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="terms" element={<TermsAndConditions />} />
        <Route path="returns" element={<ReturnsPolicy />} />
        <Route path="returns-damage" element={<ReturnsAndDamage />} />
        <Route path="damage-loss" element={<DamageLossPolicy />} />
        <Route path="cookies" element={<CookiePolicy />} />

        <Route path="sign-in" element={<SignIn />} />
        <Route path="sign-up" element={<SignUp />} />

        <Route
          path="account/*"
          element={
            <RequireAuth>
              <Account />
            </RequireAuth>
          }
        />
        {/* Checkout is open to guests — they see their items + a sign-in
            CTA. The actual place_order requires a session, which the
            component enforces internally. */}
        <Route path="checkout" element={<Checkout />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* Admin — uses its own shell, no storefront chrome */}
      <Route
        path="admin/*"
        element={
          <RequireAdmin>
            <AdminShell />
          </RequireAdmin>
        }
      />
      </Routes>

      {/* Modal overlay routes — only render when a backgroundLocation is
          present (i.e. we came from a Browse-style navigation). Browse
          stays mounted underneath, so its scroll position is preserved
          by definition. */}
      {backgroundLocation && (
        <Routes>
          <Route path="browse/:slug" element={<ItemModal />} />
        </Routes>
      )}
    </>
  );
}
